import React from 'react';
import { Card, Badge, Avatar, IconButton, Tooltip, BadgeAccentColor } from '@dopomogai/ui';
import { FileText, Share2, Phone, Copy } from 'lucide-react';

type Status = 'Funding' | 'Bookable' | 'In build' | 'Waitlist' | 'Closed';
type Variant = 'compact' | 'expanded';

type Actions = {
  onOpenCV?: () => void;
  onShare?: () => void;
  onCopyLink?: () => void;
  onBookCall?: () => void;
};

type AvatarInput = {
  src?: string;
  initials?: string;
  alt?: string;
};

export type AgentIdentityCardProps = {
  name: string;
  subtitle?: string;
  chips?: string[];
  status?: Status;
  avatar?: AvatarInput;
  actions?: Actions;
  // Visual preview thumbnails that can change per preset/package
  activePresetId?: string;
  mockImagesByPreset?: Record<string, string[]>;
  onImageClick?: (url: string) => void;

  // Behavior and styling
  variant?: Variant; // default 'compact'
  interactive?: boolean; // if true, whole card is clickable
  onClick?: () => void;
  className?: string; // applied to an outer wrapper for layout tweaks
};

const statusAccent: Record<Status, 'amber' | 'green' | 'blue' | 'violet' | 'gray'> = {
  Funding: 'amber',
  Bookable: 'green',
  'In build': 'blue',
  Waitlist: 'violet',
  Closed: 'gray',
};

export function AgentIdentityCard({
  name,
  subtitle,
  chips,
  status,
  avatar,
  actions,
  activePresetId,
  mockImagesByPreset,
  onImageClick,
  variant = 'compact',
  interactive = false,
  onClick,
  className,
}: AgentIdentityCardProps) {
  const disabled = status === 'Closed';
  const images = (activePresetId && mockImagesByPreset?.[activePresetId]) || [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  const Root = (
    <Card
      noHoverEffect={!interactive}
      // Card currently doesn’t list className/onClick in manifest; many builds still pass through.
      // To stay safe, we attach interactivity and classes to inner wrapper below.
    >
      <div
        className={[
          'flex',
          'gap-3',
          variant === 'expanded' ? 'p-5' : 'p-4',
          // Lift & Shift when interactive per design system
          interactive
            ? 'transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)] active:translate-x-0 active:translate-y-0 active:shadow-none'
            : '',
          disabled ? 'opacity-80' : '',
          className || '',
        ].join(' ')}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={handleKeyDown}
        onClick={interactive ? onClick : undefined}
        aria-label={interactive ? `Open details for ${name}` : undefined}
      >
        {variant === 'expanded' && (avatar?.src || avatar?.initials) ? (
          <div className="shrink-0">
            <Avatar
              src={avatar.src}
              initials={avatar.initials}
              alt={avatar.alt || `${name} avatar`}
              size="lg"
            />
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xl font-extrabold leading-tight text-slate-900 truncate">
                {name}
              </div>
              {subtitle ? (
                <div
                  className={[
                    'text-slate-700',
                    variant === 'compact' ? 'text-xs mt-0.5' : 'text-sm mt-1',
                    'truncate',
                  ].join(' ')}
                  title={subtitle}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>
            {status ? (
              <Badge text={status} accentColor={statusAccent[status] as BadgeAccentColor} />
            ) : null}
          </div>

          {chips && chips.length ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {chips.slice(0, variant === 'compact' ? 3 : 4).map((c, i) => (
                <Badge key={`${c}-${i}`} text={c} accentColor="gray" />
              ))}
            </div>
          ) : null}

          {images.length ? (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {images.slice(0, 4).map((src, idx) => (
                <button
                  key={`${src}-${idx}`}
                  type="button"
                  className="group relative border-2 border-black rounded-lg overflow-hidden transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_black] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  onClick={() => onImageClick?.(src)}
                  aria-label={`Preview identity image ${idx + 1}`}
                  disabled={disabled}
                >
                  <img src={src} alt="" className="w-full h-16 object-cover bg-slate-100" />
                </button>
              ))}
            </div>
          ) : null}

          {/* Actions row */}
          {actions && (actions.onOpenCV || actions.onShare || actions.onCopyLink || actions.onBookCall) ? (
            <div className="mt-3 flex items-center gap-1.5">
              <Tooltip text={disabled ? 'Closed for now' : 'Open CV'}>
                <span>
                  <IconButton
                    icon={<FileText size={18} />}
                    ariaLabel="Open CV"
                    size="sm"
                    variant="secondary"
                    onClick={actions.onOpenCV}
                    disabled={disabled}
                  />
                </span>
              </Tooltip>
              <Tooltip text={disabled ? 'Closed for now' : 'Share'}>
                <span>
                  <IconButton
                    icon={<Share2 size={18} />}
                    ariaLabel="Share"
                    size="sm"
                    variant="secondary"
                    onClick={actions.onShare}
                    disabled={disabled}
                  />
                </span>
              </Tooltip>
              <Tooltip text={disabled ? 'Closed for now' : 'Copy link'}>
                <span>
                  <IconButton
                    icon={<Copy size={18} />}
                    ariaLabel="Copy link"
                    size="sm"
                    variant="secondary"
                    onClick={actions.onCopyLink}
                    disabled={disabled}
                  />
                </span>
              </Tooltip>
              <div className="ml-auto">
                <Tooltip text={disabled ? 'Closed for now' : 'Book a call'}>
                  <span>
                    <IconButton
                      icon={<Phone size={18} />}
                      ariaLabel="Book a call"
                      size="sm"
                      variant="outline"
                      onClick={actions.onBookCall}
                      disabled={disabled}
                    />
                  </span>
                </Tooltip>
              </div>
            </div>
          ) : null}

          <div className="mt-2 text-[11px] text-slate-600">Hire once, stay forever.</div>
        </div>
      </div>
    </Card>
  );

  return Root;
}

export default AgentIdentityCard;